import { useCallback } from "react";

import { Spinner } from "tamagui";

import {
  DisabledContentColor,
  DisabledSolidStyles,
} from "@/ui/core/buttons/styles";
import type { FAIconName } from "@/ui/core/Icon";
import Icon from "@/ui/core/Icon";
import Box from "@/ui/layout/Box";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

type IconButtonVariant =
  | "accent.blue"
  | "accent.critical"
  | "primary"
  | "primarySolid";

interface IconButtonProps extends BaseViewProps {
  iconName: FAIconName;
  isDisabled?: boolean;
  isLoading?: boolean;
  onPress: () => void;
  shouldShowBorder?: boolean;
  variant?: IconButtonVariant;
}

interface IconButtonStyleAttributes {
  backgroundColor: string;
  iconButtonSize: number;
  iconColor: string;
  pressBackgroundColor: string;
}

const DefaultIconButtonSize = 40;
const ActionButtonSize = 54;

const IconColorVariantMapper: Record<
  IconButtonVariant,
  IconButtonStyleAttributes
> = {
  "accent.blue": {
    backgroundColor: ColorToken.AccentBlue,
    iconButtonSize: ActionButtonSize,
    iconColor: ColorToken.NeutralBackgroundPrimary,
    pressBackgroundColor: ColorToken.ButtonAccentBlueActive,
  },
  "accent.critical": {
    backgroundColor: ColorToken.Transparent,
    iconButtonSize: DefaultIconButtonSize,
    iconColor: ColorToken.NotificationCritical,
    pressBackgroundColor: ColorToken.NotificationCriticalBackground,
  },
  primary: {
    backgroundColor: ColorToken.Transparent,
    iconButtonSize: DefaultIconButtonSize,
    iconColor: ColorToken.Primary100,
    pressBackgroundColor: ColorToken.Primary10,
  },
  primarySolid: {
    backgroundColor: ColorToken.Primary100,
    iconButtonSize: DefaultIconButtonSize,
    iconColor: ColorToken.NeutralBackgroundPrimary,
    pressBackgroundColor: ColorToken.ButtonPrimaryActive,
  },
};

const IconButton = ({
  accessibilityLabel,
  iconName,
  isDisabled,
  isLoading,
  onPress,
  shouldShowBorder = false,
  testID,
  variant = "primary",
}: IconButtonProps) => {
  const { backgroundColor, iconButtonSize, iconColor, pressBackgroundColor } =
    IconColorVariantMapper[variant];

  const handlePress = useCallback(() => {
    if (!isDisabled && !isLoading) {
      onPress();
    }
  }, [isDisabled, isLoading, onPress]);

  const baseTestID = joinTestIDs(testID, "icon-button");

  return (
    <Box
      alignItems="center"
      aria-label={accessibilityLabel}
      backgroundColor={backgroundColor}
      borderColor={shouldShowBorder ? iconColor : ColorToken.Transparent}
      borderRadius={iconButtonSize / 2}
      borderWidth={shouldShowBorder ? 1 : 0}
      disabled={isDisabled}
      disabledStyle={{
        backgroundColor: DisabledSolidStyles?.backgroundColor,
      }}
      height={iconButtonSize}
      justifyContent="center"
      onPress={handlePress}
      pressStyle={{
        backgroundColor: pressBackgroundColor,
      }}
      role="button"
      testID={baseTestID}
      width={iconButtonSize}
    >
      {isLoading ? (
        <Spinner color={iconColor} size="small" />
      ) : (
        <Icon
          color={isDisabled ? DisabledContentColor : iconColor}
          name={iconName}
          size="lg"
          testID={baseTestID}
        />
      )}
    </Box>
  );
};

export default IconButton;
export { DefaultIconButtonSize as IconButtonSize };
export type { IconButtonProps, IconButtonVariant };
